import React, {lazy, Suspense} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import NotFound from '../pages/PageNotFound';
import OnboardingSuccessDialog, {
  type OnboardingSuccessVariant,
} from '../pages/onboarding/shared/OnboardingSuccessDialog';
import PartnerReducer, {
  initialServices,
} from '../pages/partners/partner_form/PartnerReducer';
import PartnerContactReducer, {
  initialOnboardContact,
} from '../pages/partners/partner_form/PartnerContactReducer';
import {CanRouteAny} from '../components/UserCan';
import {useUserCan, useUserCanAny} from '../hooks/useUserCan';
import {CircularProgress} from '@mui/material';
import {
  onboardingWorkflowAccountChangeScope,
  onboardingWorkflowClosedWonScope,
  onboardingWorkflowDraftScope,
  onboardingWorkflowMenuScope,
  onboardingWorkflowNewPartnerScope,
  onboardingWorkflowReviewScope,
  onboardingWorkflowSuccessScope,
} from '../pages/onboarding/onboardingPermssionScope';

const ProductPartner = lazy(
  () => import('../pages/partners/partner/ProductPartner'),
);
const SearchPartner = lazy(
  () => import('../pages/partners/searchPartner/SearchPartner'),
);
const NewPartner = lazy(
  () => import('../pages/partners/partner_form/NewPartner'),
);
const PartnerContacts = lazy(
  () => import('../pages/partners/partner_form/ParnterContacts'),
);
const AddOnDetails = lazy(
  () => import('../pages/partners/add-on/AddOnDetailsPage'),
);
const DealImportPage = lazy(
  () => import('../pages/onboarding/import/DealImportPage'),
);
const DraftManagementPage = lazy(
  () => import('../pages/onboarding/import/DraftManagementPage'),
);
const AccountChangeEntryPage = lazy(
  () => import('../pages/onboarding/account-change/AccountChangeEntryPage'),
);
const AccountChangeFlowPage = lazy(
  () => import('../pages/onboarding/account-change/AccountChangeFlowPage'),
);
const AccountChangeSuccessPage = lazy(
  () => import('../pages/onboarding/account-change/AccountChangeSuccessPage'),
);
const RedesignFlowPage = lazy(
  () => import('../pages/onboarding/account-change/RedesignFlowPage'),
);
// Phase 3 lazy-loaded routes
const SpecialistReviewScreen = lazy(
  () => import('../pages/onboarding/specialist/SpecialistReviewScreen'),
);
const IndividualStepper = lazy(
  () => import('../pages/onboarding/individual/IndividualStepper'),
);
const DistrictStepper = lazy(
  () => import('../pages/onboarding/district/DistrictStepper'),
);

export default function OnboardingRoutes() {
  return (
    <Routes>
      <Route index element={<OnboardingIndexRoute />} />
      <Route path='legacy' element={<LegacyProductPartnerRoute />} />
      <Route path='new/*' element={<NewOnboardingPartner />} />
      <Route path='import' element={<ImportRouteGuard />} />
      <Route
        path='import/account-change'
        element={<AccountChangeEntryRouteGuard />}
      />
      <Route
        path='import/account-change/:workflowId'
        element={<RedesignFlowRouteGuard />}
      />
      <Route
        path='import/account-change/:workflowId/success'
        element={<RedesignSuccessRouteGuard />}
      />
      <Route
        path='import/account-change/v2/:workflowId'
        element={<AccountChangeFlowRouteGuard />}
      />
      <Route
        path='import/account-change/v2/:workflowId/success'
        element={<AccountChangeSuccessRouteGuard />}
      />
      <Route path='import/draft/:workflowId' element={<DraftRouteGuard />} />
      {/* Phase 3 routes */}
      <Route
        path='import/review/:workflowId'
        element={<SpecialistReviewRouteGuard />}
      />
      <Route
        path='import/individual/:workflowId'
        element={<IndividualStepperRouteGuard />}
      />
      <Route
        path='import/district/:workflowId'
        element={<DistrictStepperRouteGuard />}
      />
      <Route
        path='import/schoolzone/:workflowId'
        element={<IndividualStepperRouteGuard />}
      />
      <Route
        path='import/submitted/:workflowId'
        element={<LegacySuccessRouteGuard successVariant='submitted' />}
      />
      <Route
        path='import/success/:workflowId'
        element={<LegacySuccessRouteGuard successVariant='accounts-created' />}
      />
      <Route path=':company_id/add-on/*' element={<AddOnPartner />} />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

const OnboardingIndexRoute = () => {
  const hasImportAccess = useUserCanAny(onboardingWorkflowMenuScope);

  if (hasImportAccess) {
    return <Navigate to='import' replace />;
  }

  return <LegacyProductPartnerRoute />;
};

const LegacyProductPartnerRoute = () => {
  return (
    <Suspense fallback={<CircularProgress />}>
      <ProductPartner />
    </Suspense>
  );
};

const ImportRouteGuard = () => {
  const hasPermission = useUserCanAny(onboardingWorkflowMenuScope);
  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowMenuScope}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <DealImportPage />
    </Suspense>
  );
};

const DraftRouteGuard = () => {
  const isAdmin = useUserCan('is-admin');
  const canUseClosedWonFlow = useUserCan(
    'onboarding-workflow:create,onboarding-workflow:closed-won',
  );
  const canUseNewPartnerFlow = useUserCan(
    'onboarding-workflow:create,onboarding-workflow:new-partner',
  );
  const hasPermission = isAdmin || canUseClosedWonFlow || canUseNewPartnerFlow;
  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowDraftScope}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <DraftManagementPage />
    </Suspense>
  );
};

const AccountChangeEntryRouteGuard = () => {
  const isAdmin = useUserCanAny('is-admin');
  const canUpdateOnboarding = useUserCan('onboarding-workflow:update');
  const canUseAccountChange = useUserCan('onboarding-workflow:account-change');
  const hasPermission = isAdmin || (canUpdateOnboarding && canUseAccountChange);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowAccountChangeScope}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <AccountChangeEntryPage />
    </Suspense>
  );
};

const AccountChangeFlowRouteGuard = () => {
  const isAdmin = useUserCanAny('is-admin');
  const canUpdateOnboarding = useUserCan('onboarding-workflow:update');
  const canUseAccountChange = useUserCan('onboarding-workflow:account-change');
  const hasPermission = isAdmin || (canUpdateOnboarding && canUseAccountChange);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowAccountChangeScope}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <AccountChangeFlowPage />
    </Suspense>
  );
};

const AccountChangeSuccessRouteGuard = () => {
  const isAdmin = useUserCanAny('is-admin');
  const canUpdateOnboarding = useUserCan('onboarding-workflow:update');
  const canUseAccountChange = useUserCan('onboarding-workflow:account-change');
  const hasPermission = isAdmin || (canUpdateOnboarding && canUseAccountChange);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowAccountChangeScope}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <AccountChangeSuccessPage />
    </Suspense>
  );
};

const RedesignFlowRouteGuard = () => {
  const isAdmin = useUserCanAny('is-admin');
  const canUpdateOnboarding = useUserCan('onboarding-workflow:update');
  const canUseAccountChange = useUserCan('onboarding-workflow:account-change');
  const hasPermission = isAdmin || (canUpdateOnboarding && canUseAccountChange);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowAccountChangeScope}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <RedesignFlowPage />
    </Suspense>
  );
};

const RedesignSuccessRouteGuard = () => {
  const isAdmin = useUserCanAny('is-admin');
  const canUpdateOnboarding = useUserCan('onboarding-workflow:update');
  const canUseAccountChange = useUserCan('onboarding-workflow:account-change');
  const hasPermission = isAdmin || (canUpdateOnboarding && canUseAccountChange);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowAccountChangeScope}>
        <></>
      </CanRouteAny>
    );
  }

  return <Navigate to='/partner/import/account-change' replace />;
};

/**
 * Phase 3 route guard for specialist review screen.
 * Gated behind the specialist review onboarding scopes.
 */
const SpecialistReviewRouteGuard = () => {
  const hasPermission = useUserCanAny(onboardingWorkflowReviewScope);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowReviewScope}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <SpecialistReviewScreen />
    </Suspense>
  );
};

const IndividualStepperRouteGuard = () => {
  const isAdmin = useUserCan('is-admin');
  const canUseClosedWonFlow = useUserCan(
    'onboarding-workflow:create,onboarding-workflow:closed-won',
  );
  const hasPermission = isAdmin || canUseClosedWonFlow;

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowClosedWonScope}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <IndividualStepper />
    </Suspense>
  );
};

const DistrictStepperRouteGuard = () => {
  const isAdmin = useUserCan('is-admin');
  const canUseNewPartnerFlow = useUserCan(
    'onboarding-workflow:create,onboarding-workflow:new-partner',
  );
  const hasPermission = isAdmin || canUseNewPartnerFlow;

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowNewPartnerScope}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <DistrictStepper />
    </Suspense>
  );
};

const LegacySuccessRouteGuard = ({
  successVariant,
}: {
  successVariant: OnboardingSuccessVariant;
}) => {
  const hasPermission = useUserCanAny(onboardingWorkflowSuccessScope);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={onboardingWorkflowSuccessScope}>
        <></>
      </CanRouteAny>
    );
  }

  return <OnboardingSuccessDialog variant={successVariant} />;
};

const NewOnboardingPartner = () => {
  const [state, dispatch] = React.useReducer(PartnerReducer, initialServices);
  const [contactState, contactDispatch] = React.useReducer(
    PartnerContactReducer,
    initialOnboardContact,
  );

  return (
    <Routes>
      <Route
        path=''
        element={
          <SearchPartner
            dispatch={dispatch}
            contactDispatch={contactDispatch}
          />
        }
      />
      <Route
        path='form'
        element={<NewPartner state={state} dispatch={dispatch} />}
      />
      <Route
        path='contacts'
        element={
          <PartnerContacts
            onBoardState={state}
            onBoardDispatch={dispatch}
            contactState={contactState}
            contactDispatch={contactDispatch}
          />
        }
      />
    </Routes>
  );
};

const AddOnPartner = () => {
  const [state, dispatch] = React.useReducer(PartnerReducer, initialServices);
  const [contactState, contactDispatch] = React.useReducer(
    PartnerContactReducer,
    initialOnboardContact,
  );

  return (
    <Routes>
      <Route path='' element={<AddOnDetails />} />
      <Route
        path='form'
        element={<NewPartner state={state} dispatch={dispatch} />}
      />
      <Route
        path='contacts'
        element={
          <PartnerContacts
            onBoardState={state}
            onBoardDispatch={dispatch}
            contactState={contactState}
            contactDispatch={contactDispatch}
          />
        }
      />
    </Routes>
  );
};
